import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../Layout';
import { LoisirManager } from '../../helpers/loisir';
import { showToast } from '../../helpers/customToast';
import { Calendar, Package, Check, Truck, Clock, Shield, PartyPopper } from 'lucide-react';
import '../../css/loisir.css';

const LoisirListingPage = () => {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFormulas = async () => {
      try {
        setLoading(true);
        const data = await LoisirManager.fetchFormulas();
        // console.log(data)
        setFormulas(data);
      } catch (error) {
        console.error('Error fetching formulas:', error);
        showToast('Erreur lors du chargement des formules', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchFormulas();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="container my-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des formules...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container my-5">
        <div className="title-section mb-5">
          <div className="title-section-bg">
            <div className="container">
              <h1 className="text-white">LOISIRS</h1>
              <p className="text-white lead">
                Vous êtes à deux pas de rejoindre l'aventure des parents baroudeurs...
              </p>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title">Choisissez votre formule sans engagement de durée</h3>
                <p className="card-text">
                  Nos formules de location de jouets vous permettent de varier les plaisirs pour vos enfants, 
                  tout en adoptant une démarche éco-responsable.
                </p>

                <div className="table-responsive mt-4">
                  <table className="table table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th></th>
                        {formulas.length > 0 && formulas[0].components_pricing_formula_prices
                          .sort((a, b) => a.duration_days - b.duration_days)
                          .map((priceItem) => (
                            <th
                              key={priceItem.duration_days}
                              colSpan="2"
                              className={`text-center ${priceItem.duration_days === 30 ? 'bg-light' : ''}`}
                            >
                              {priceItem.duration_days} JOURS
                              {priceItem.duration_days === 30 && (
                                <span className="badge bg-success ms-1">GARANTIES+</span>
                              )}
                            </th>
                          ))
                        }
                      </tr>
                      <tr>
                        <th></th>
                        {formulas.length > 0 && formulas[0].components_pricing_formula_prices
                          .sort((a, b) => a.duration_days - b.duration_days)
                          .map((priceItem) => (
                            <React.Fragment key={`headers-${priceItem.duration_days}`}>
                              <th className="text-center">PRIX</th>
                              <th className="text-center">CAUTION</th>
                            </React.Fragment>
                          ))
                        }
                      </tr>
                    </thead>
                    <tbody>
                      {formulas.map((formula) => (
                        <tr key={formula.id}>
                          <td>
                            <strong>{formula.name}</strong>
                            <br />
                            <small>{formula.article_quantity} articles au choix</small>
                          </td>
                          {formula.components_pricing_formula_prices.sort((a, b) => a.duration_days - b.duration_days).map((price) => (
                            <React.Fragment key={`${formula.id}-${price.duration_days}`}>
                              <td className="text-center">{price.price.toFixed(2)}€</td>
                              <td className="text-center">{price.deposit.toFixed(2)}€</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Garanties supplémentaires pour la formule 30 jours */}
                <div className="mt-4 p-3 bg-light border border-success rounded">
                  <h5 className="mb-3">
                    <span className="badge bg-success">NOUVEAU</span>
                    &nbsp;Garanties exclusives pour la formule 30 jours
                  </h5>
                  <div className="row">
                    <div className="col-md">
                      <div className="d-flex align-items-center mb-2">
                        <Truck size={20} className="text-success me-2" />
                        <span>Transport offert (un échange/mois)</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <Clock size={20} className="text-success me-2" />
                        <span>Renvoi flexible sans calendrier</span>
                      </div>
                    </div>
                    <div className="col-md">
                      <div className="d-flex align-items-center mb-2">
                        <Shield size={20} className="text-success me-2" />
                        <span>Casse & usure incluses</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <PartyPopper size={20} className="text-success me-2" />
                        <span>Invitation aux après-midi et soirées privées</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-md-4 mb-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body text-center p-4">
                <div className="mb-3">
                  <Package size={48} className="text-primary" />
                </div>
                <h4 className="card-title">Les formules comprennent</h4>
                <ul className="list-unstyled mt-3">
                  <li className="mb-2">
                    <Check size={18} className="me-2 text-success" />
                    Sans engagement de durée
                  </li>
                  <li className="mb-2">
                    <Check size={18} className="me-2 text-success" />
                    Contrôle et nettoyage inclus
                  </li>
                  <li className="mb-2">
                    <Check size={18} className="me-2 text-success" />
                    Service client réactif
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div className="card h-100 shadow-sm">
              <div className="card-body p-4">
                <h4 className="card-title mb-4">Comment ça marche ?</h4>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="d-flex">
                      <div className="step-circle">1</div>
                      <div className="ms-3">
                        <h5>Je choisis ma formule</h5>
                        <p className="text-muted">Sélectionnez la formule qui correspond à vos besoins.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex">
                      <div className="step-circle">2</div>
                      <div className="ms-3">
                        <h5>Je choisis la durée</h5>
                        <p className="text-muted">Et je sélectionne mes articles préférés.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex">
                      <div className="step-circle">3</div>
                      <div className="ms-3">
                        <h5>Je reçois mes jouets</h5>
                        <p className="text-muted">Selon le mode de livraison choisi.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-4">
                  <Link to="/loisirs/selection" className="btn btn-primary btn-lg">
                    <Calendar className="me-2" />
                    Commencer ma sélection
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-5 bg-light">
          <div className="card-body p-4">
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <img src="/assets/eco.jpg" alt="Éco-responsable" width="64" />
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="card-title mb-1">Notre démarche éco-responsable</h5>
                <p className="card-text">
                  Nous souhaitons rendre notre service aussi responsable et respectueux de l'environnement que possible.
                  En louant des jouets plutôt qu'en les achetant, vous contribuez à réduire les déchets et à préserver les ressources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoisirListingPage;